import cx from 'classix';
import { type LinkProps } from 'next/link';
import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type RefObject,
} from 'react';
import { FaCheck } from 'react-icons/fa6';

import { Link } from '@/i18n/routing';
import Loader from '@/ui/loader/loader';

import styles from './button.module.scss';

type ButtonVariant = 'outlined' | 'filled' | 'ghost';
type ButtonSize = 'lg' | 'md' | 's';
type ButtonFontSize = 'l' |'m' | 's' | 'xs';
type ButtonShade = 'dark' | 'light' | 'danger';

interface ButtonBaseProps {
  size?: ButtonSize,
  fullWidth?: boolean,
  variant?: ButtonVariant,
  shade?: ButtonShade,
  fontSize?: ButtonFontSize,
  isLoading?: boolean,
  isSuccess?: boolean,
  successMessage?: string,
  ref?: RefObject<HTMLButtonElement | null>;
  withFocus?: boolean;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonBaseProps {
  element: 'button';
}

type NextLink = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & LinkProps;

// type HtmlLink = AnchorHTMLAttributes<HTMLAnchorElement>;

export interface ButtonLinkProps extends NextLink, ButtonBaseProps {
  element: 'link';
}

export interface ButtonNativeLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement>, ButtonBaseProps {
  element: 'nativeLink';
}

export default function Button(props: ButtonProps | ButtonLinkProps | ButtonNativeLinkProps) {
  const {
    children,
    className,
    element,
    fontSize = 'm',
    fullWidth,
    isLoading = false,
    isSuccess = false,
    onClick,
    shade = 'dark',
    size = 'md',
    successMessage = null,
    variant = 'filled',
    withFocus = false,
  } = props;

  const buttonStyles = cx(
    styles.base,
    styles[`size_${size}`],
    styles[`variant_${variant}`],
    styles[`shade_${shade}`],
    styles[`font_${fontSize}`],
    fullWidth && styles.size_full,
    isSuccess && styles.base__isSuccess,
    withFocus && styles.base__withFocus,
    isLoading && styles.base__isLoading,
    className,
  );

  if (element === 'link') {
    const {
      href,
      prefetch,
      onNavigate,
    } = props;

    return (
      <Link
        className={buttonStyles}
        href={href}
        prefetch={prefetch}
        onClick={onClick}
        onNavigate={onNavigate}
      >
        {children}
      </Link>
    );
  }

  if (element === 'nativeLink') {
    const { href } = props;

    return (
      <a
        className={buttonStyles}
        href={href}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  const {
    disabled,
    name,
    ref,
    type = 'button',
  } = props;

  return (
    (
      <button
        ref={ref}
        className={buttonStyles}
        name={name}
        onClick={onClick}
        type={type}
        disabled={disabled}
        aria-disabled={disabled}
        aria-label={name}
      >
        {isSuccess ? (
          successMessage ? (
            <span className={styles.buttonSuccessMessage}>{successMessage}</span>
          ) : (
            (<FaCheck className={styles.buttonSuccessIcon} />)
          )
        ) : (
          isLoading ? <Loader /> : children
        )}
      </button>
    )
  );
}
